import z from "zod";
import { getEnv } from "@/env";

export type PasswordPolicyConfig = {
    minLength: number;
    enforceComplexity: boolean;
    enforceUncompromised: boolean;
};

const parseBool = (value: string, fallback: boolean): boolean => {
    if (value === "") {
        return fallback;
    }

    return value === "true" || value === "1";
};

export const passwordMessages = {
    minLength: (length: number) =>
        `Password must be at least ${length} characters long.`,
    mixedCase:
        "Password must contain at least one uppercase and one lowercase letter.",
    numbers: "Password must contain at least one number.",
    symbols: "Password must contain at least one special character.",
    uncompromised:
        "Password has appeared in a data breach. Please choose a different password.",
};

export const getPasswordPolicyConfig = (): PasswordPolicyConfig => {
    const minLength = Number(getEnv("VITE_PASSWORD_POLICY_MIN_LENGTH", "12"));

    return {
        minLength: Number.isNaN(minLength) ? 12 : minLength,
        enforceComplexity: parseBool(
            getEnv("VITE_PASSWORD_POLICY_ENFORCE", "true"),
            true,
        ),
        enforceUncompromised: parseBool(
            getEnv("VITE_PASSWORD_POLICY_UNCOMPROMISED", "true"),
            true,
        ),
    };
};

export const evaluatePassword = (
    password: string,
    config: PasswordPolicyConfig = getPasswordPolicyConfig(),
) => {
    const meetsLength = password.length >= config.minLength;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);

    const checks = [
        {
            key: "length",
            label: passwordMessages.minLength(config.minLength),
            satisfied: meetsLength,
        },
    ];

    if (config.enforceComplexity) {
        checks.push({
            key: "mixedCase",
            label: passwordMessages.mixedCase,
            satisfied: hasUppercase && hasLowercase,
        });
        checks.push({
            key: "numbers",
            label: passwordMessages.numbers,
            satisfied: hasNumber,
        });
        checks.push({
            key: "symbols",
            label: passwordMessages.symbols,
            satisfied: hasSymbol,
        });
    }

    return {
        checks,
        isValid: checks.every((check) => check.satisfied),
    };
};

export const createPasswordSchema = (
    config: PasswordPolicyConfig = getPasswordPolicyConfig(),
) => {
    let schema = z.string().min(config.minLength, {
        message: passwordMessages.minLength(config.minLength),
    });

    if (config.enforceComplexity) {
        schema = schema
            .regex(/[A-Z]/, { message: passwordMessages.mixedCase })
            .regex(/[a-z]/, { message: passwordMessages.mixedCase })
            .regex(/[0-9]/, { message: passwordMessages.numbers })
            .regex(/[^A-Za-z0-9]/, { message: passwordMessages.symbols });
    }

    return schema;
};
